import type { CampaignsAgentContext } from "./agents/insights/types";
// Place things imported from the frontend that need to avoid backend dependencies
import type { CitationScope } from "./agentic-settings-entities/citation-syntax";
import type { LLMClientConfig } from "./clients/types";
import type { SuggestionsMessage } from "./schema";
import type { FeedbackItem } from "./types";
import {
  AGENT_SCOPE,
  type AgentScope,
  type ContextFileType,
} from "./agentic-settings-entities/types";
import type { AssetTypeDefinition } from "../agents/asset-type-definition";
import type { LcmCampaignConfig } from "./lifecycle-studio/types";
import type { CampaignInsightsSettings } from "../campaign-insights/definition-selections";

export type { AssetTypeDefinition } from "../agents/asset-type-definition";

export const DEFAULT_MODEL = "claude-opus-5-5";
export const DEFAULT_LIGHTWEIGHT_MODEL = "claude-sonnet-4-6";

export enum AgentType {
  Audience = "audience",
  Marketing = "marketing",
  Campaigns = "campaigns",
  Routing = "routing",
  Content = "content",
  BrandKit = "brand-kit",
  AdStudioAdGenerator = "ad-studio-ad-generator",
  AdStudioTemplateGenerator = "ad-studio-template-generator",
  ContentNodeEditing = "content-node-editing",
  EmailBestPractice = "email-best-practice",
  EmailLayerExport = "email-layer-export",
  EmailComponentCanonicalization = "email-component-canonicalization",
  EmailComponentExtraction = "email-component-extraction",
  LCM = "lcm",
  FigmaImport = "figma-import",
  InitiativeInsights = "initiative-insights",
  CampaignCalendarEnrichment = "campaign-calendar-enrichment",
  Suggestions = "suggestions",
  ReportSuggestions = "report-suggestions",
  CampaignInsights = "campaign-insights",
  ContextAgent = "context-agent",
  // A project or task sub-agent that creates syncs from sync templates.
  Sync = "sync",

  // no longer supported, but still on type for backwards compatibility for old chats
  ReportPromptGeneration = "report-prompt-generation",
  AdStudioStyleCurator = "ad-studio-style-curator",
  LcmStyleCurator = "lcm-style-curator",
  LcmStyleExampleGenerator = "lcm-style-example-generator",
  Copilot = "copilot",
  ArtifactSearch = "artifact-search",
  CampaignEnrichment = "campaign-enrichment",
  Trait = "trait",
  Journey = "journey",
  Idr = "idr",
  ContextReview = "context-review",
}

// Maps an agent type to the context-file scope it reads. Ads-family agents
// (Ad Studio, brand kit, campaigns) see "ads"-scoped files; lifecycle/content
// agents see "lcm"-scoped files; the reporting agents see "data"-scoped files.
// Every other agent returns null, meaning the context-file scope filter is
// skipped entirely so that agent sees all files.
export function agentTypeToScope(agentType: AgentType): AgentScope | null {
  switch (agentType) {
    case AgentType.Campaigns:
    case AgentType.BrandKit:
    case AgentType.AdStudioAdGenerator:
    case AgentType.AdStudioStyleCurator:
    case AgentType.AdStudioTemplateGenerator:
      return AGENT_SCOPE.ads;
    case AgentType.LCM:
    case AgentType.Content:
    case AgentType.ContentNodeEditing:
    case AgentType.LcmStyleCurator:
    case AgentType.LcmStyleExampleGenerator:
    case AgentType.EmailBestPractice:
    case AgentType.EmailLayerExport:
    case AgentType.EmailComponentCanonicalization:
    case AgentType.EmailComponentExtraction:
    case AgentType.FigmaImport:
      return AGENT_SCOPE.lcm;
    case AgentType.ReportSuggestions:
    case AgentType.CampaignInsights:
    case AgentType.ReportPromptGeneration:
    case AgentType.InitiativeInsights:
    case AgentType.CampaignCalendarEnrichment:
    case AgentType.Suggestions:
      return AGENT_SCOPE.data;
    default:
      return null;
  }
}

export const AGENT_MODES = ["auto", "plan"] as const;

export type AgentMode = (typeof AGENT_MODES)[number];

export function resolveAgentMode(mode: unknown): AgentMode {
  return mode === "plan" ? "plan" : "auto";
}

/**
 * The Ad Studio beta video model: gpt-6 through the LiteLLM gateway. The id
 * names its effort and service tier because the Anthropic Messages format the
 * agent speaks carries neither; see bedrock-agent-service/litellm/config.yaml.
 */
export const VIDEO_ENGINE_MODEL =
  "openai/gpt-6-astra-low-effort-default-service";

export const AD_STUDIO_STYLE_MODES = ["auto", "none"] as const;

export type AdStudioStyleMode = (typeof AD_STUDIO_STYLE_MODES)[number];

export function resolveAdStudioStyleMode(
  mode: unknown,
): AdStudioStyleMode | undefined {
  return mode === "auto" || mode === "none" ? mode : undefined;
}

export const AD_STUDIO_TEMPLATE_MODE = {
  AdStudio: "template",
  EnrichedCatalog: "enriched-catalog",
} as const;

export type AdStudioTemplateMode =
  (typeof AD_STUDIO_TEMPLATE_MODE)[keyof typeof AD_STUDIO_TEMPLATE_MODE];

export const AD_STUDIO_TEMPLATE_OPERATION = {
  Create: "create",
  Edit: "edit",
} as const;

export type AdStudioTemplateOperation =
  (typeof AD_STUDIO_TEMPLATE_OPERATION)[keyof typeof AD_STUDIO_TEMPLATE_OPERATION];

export const AD_STUDIO_TEMPLATE_IMPORT_SOURCE = {
  Ad: "ad",
  Figma: "figma",
  Html: "html",
  Image: "image",
  Psd: "psd",
} as const;

export type AdStudioTemplateImportSource =
  (typeof AD_STUDIO_TEMPLATE_IMPORT_SOURCE)[keyof typeof AD_STUDIO_TEMPLATE_IMPORT_SOURCE];

export type AdStudioTemplateSourceAd = {
  assetId: string;
  assetVersionId: string;
};

export enum SkillName {
  AnswerEngine = "answer-engine",
  Canvas = "canvas",
  AgentCollaboration = "agent-collaboration",
  ActivityMemory = "activity-memory",
  AgentMonitors = "agent-monitors",
  Personalization = "personalization",
  CampaignCalendarEngagement = "campaign-calendar-engagement",
  CampaignCalendarEnrichment = "campaign-calendar-enrichment",
  CopyWriting = "copy-writing",
  CreateCopyArtifact = "create-copy-artifact",
  EmailGeneration = "email-generation",
  EspConditions = "esp-conditions",
  CsvExport = "csv-export",
  PptxExport = "pptx-export",

  // Campaign Data Querying Skills
  CampaignChannelDataQuerying = "campaign-channel-data-querying", // querying ads channel data
  LcmChannelDataQuerying = "lcm-channel-data-querying", // querying LCM channel data
  FacebookDataQuerying = "facebook-data-querying",
  GoogleDataQuerying = "google-data-querying",
  BingDataQuerying = "bing-data-querying",
  LinkedinDataQuerying = "linkedin-data-querying",
  SnapchatDataQuerying = "snapchat-data-querying",
  TikTokDataQuerying = "tiktok-data-querying",
  PinterestDataQuerying = "pinterest-data-querying",
  RedditDataQuerying = "reddit-data-querying",
  AmazonAdsDataQuerying = "amazon-ads-data-querying",
  SearchAds360DataQuerying = "search-ads-360-data-querying",

  // Campaign Analysis Skills
  ExploratoryAnalysis = "exploratory-analysis",
  CampaignSuggestions = "campaign-suggestions",

  // Campaign Formatting Skills
  CampaignReportStructure = "campaign-report-structure",
  CampaignFunnelDisplay = "campaign-funnel-display",
  CampaignOutputComponents = "campaign-output-components",
  CampaignAdListFormats = "campaign-ad-list-formats",

  // Agentic LCM Skills
  LcmCampaignArtifact = "lcm-campaign-artifact",
  LcmCampaignMessagePlan = "lcm-campaign-message-plan",
  ExtractCopyExamples = "extract-copy-examples",

  // Email Template Skills
  EmailTemplateSelector = "email-template-selector",

  // Content Review Skill
  Review = "review",

  // A review agent replying to an @mention on an ad's comment thread
  AdAssetReview = "ad-asset-review",

  // Hightouch's agent working in a project or task session
  ProjectManagement = "project-management",

  // A file's own agent answering the comments on that file
  FileComments = "file-comments",

  // Hightouch's agent finding, making, and organizing Library files
  Library = "library",

  // The sync agent's tools for creating syncs from sync templates
  Sync = "sync",

  // Web Search Skill
  WebSearch = "web-search",

  // Data Visualization Skill
  DataVisualization = "data-visualization",

  // Custom HTML Skill — hand-authored HTML/SVG report sections beyond the standard components
  CustomHtml = "custom-html",

  // Unified Audience Skill (v3)
  AudienceV3 = "audience",

  // Brand Context Skill
  SaveBrandContext = "save-brand-context",

  // Reads, edits, troubleshoots and reviews workspace context through review-gated
  // context suggestions, in every interactive chat that can approve them. Context Hub's
  // variant adds the onboarding procedure.
  ContextManagement = "context-management",

  // Action Plan Skill: proposes a plan of tool calls for the user to approve before they run.
  ActionPlan = "action-plan",

  // Action Execution Skill: acts on connected systems, such as destinations, through connector tools.
  ActionExecution = "action-execution",

  // Connector skills: how to launch ads with the ad launch connectors, such as Meta. They load only with the connector.
  AdLaunch = "ad-launch",
  MetaAdLaunch = "meta-ad-launch",

  // Ads Brand Kit Skill
  AdsBrandKit = "ads-brand-kit",

  // Image Editing Skill
  ImageEditing = "image-editing",

  // GIF Generation Skill
  GifGeneration = "gif-generation",

  // Competitor Research Skill
  CompetitorResearch = "competitor-research",

  // Identity Resolution
  Idr = "idr",

  // Reverse ETL sync read tools
  SyncRead = "sync-read",

  // Native Delivery insights
  NativeDeliveryInsights = "native-delivery-insights",

  // Journey read and operational analysis tools
  JourneyAnalysis = "journey-analysis",

  // Reverse ETL sync lifecycle write tools
  SyncWrite = "sync-write",

  // Hightouch Experiment (split-test) results analysis
  ExperimentAnalysis = "experiment-analysis",

  Marketing = "marketing",
  Assumptions = "assumptions",
  Clarification = "clarification",
  Campaigns = "campaigns",
  Journey = "journey",

  // Ad Studio Ad Generation Skills
  AdGenerationSystemPrompt = "ad-generation-agent-system-prompt",
  AdCopywriting = "ad-copywriting",
  AdIdeation = "ad-ideation",
  AdProductAccuracy = "ad-product-accuracy",
  AdVideo = "ad-video",
  AdDesignFromVideoReference = "ad-design-from-video-reference",
  AdScriptwriting = "ad-scriptwriting",
  AdMotionDesign = "ad-motion-design",
  AdVideoGeneration = "ad-video-generation",
  AdCaptions = "ad-captions",
  WebPage = "web-page",
  AdVideoComposition = "ad-video-composition",
  AdStudioFatigueAnalysis = "ad-fatigue-analysis",
  AdReferenceLookup = "ad-reference-lookup",
  AdStudioCreativeBoost = "ad-studio-creative-boost",
  AdStudioTemplateGenerator = "ad-studio-template-generator",

  // Content Node Editing Skill
  ContentNodeEditing = "content-node-editing",

  // Email Best Practice Skill
  EmailBestPractice = "email-best-practice",

  EmailLayerExport = "email-layer-export",

  // Component Canonicalization Skill — non-deterministic normalization pass that
  // maps imported component responsive CSS onto the canonical ht-* vocabulary.
  EmailComponentCanonicalization = "email-component-canonicalization",

  // Component Extraction Skill — extracts a marked element (plus its responsive
  // twins and ancestor scaffolding) from a full source document and canonicalizes
  // it into a single headless ht-* fragment in one agent run.
  EmailComponentExtraction = "email-component-extraction",

  // Report Skills
  EditReportPrompt = "edit-report-prompt",

  // Session Reading Skill
  SessionReading = "session-reading",

  // Campaign calendar insight provenance: the insight rows and the redacted
  // analysis session behind them
  CampaignInsights = "campaign-insights",
  CalendarDefinitions = "calendar-definitions",
  CalendarContext = "calendar-context",

  // Signals report card emission skill
  SignalsEmit = "signals-emit",

  // Ad Studio ideas feed emission skill
  IdeasEmit = "ideas-emit",

  // Ad Studio opportunities feed emission skill
  OpportunitiesEmit = "opportunities-emit",

  // Ad Studio: judges ads, concepts and campaigns against the workspace's winner criteria
  WinnerCriteria = "winner-criteria",
  // Ad Studio launches report: saves the winner-criteria verdicts for the run
  LaunchesEmit = "launches-emit",

  // AID Skills
  AIDAnalysis = "aid-analysis",
  AIDContentRecommendations = "aid-content-recommendations",

  // Figma → Lifecycle Studio email-template import skill
  ImportFigmaDesign = "import-figma-design",

  // Initiative insight generation skill
  InitiativeInsights = "initiative-insights",

  // Workspace catalog: inventory of the workspace's Hightouch data as skill files
  GetHightouchCatalog = "get-hightouch-catalog",

  // Background suggestion generation (warehouse tools + suggestion instructions)
  SuggestionGeneration = "suggestion-generation",

  // Background report-suggestion generation (Reports page)
  ReportSuggestionGeneration = "report-suggestion-generation",

  // Background campaign insights generation (campaign calendar)
  CampaignInsightsGeneration = "campaign-insights-generation",
}

export enum InteractionCancelReason {
  Superseded = "superseded",
  UserCanceled = "user_canceled",
}

/**
 * Represents where an agent session was created from, usually the page or app area.
 */
export enum CreationSource {
  AmpCanvas = "amp-canvas",
  Agents = "agents",
  CampaignCalendar = "campaign-calendar",
  Audiences = "audiences",
  /** User-initiated sessions from the identity graph page's agent sidebar. */
  Idr = "idr",
  /** User-initiated sessions from the journey page's agent sidebar. */
  Journeys = "journeys",
  Mcp = "mcp",
  A2a = "a2a",
  LifecycleStudio = "lifecycle-studio",
  AdStudio = "ad-studio",
  /** User-initiated Ad Studio project created from an insights signal report. */
  AdStudioSignals = "ad-studio-signals",
  LifecycleStudioSystem = "lifecycle-studio-system",
  AdStudioSystem = "ad-studio-system",
  /** User-initated sessions on the reports page */
  Reports = "reports",
  /** Manual or scheduled execution of a saved report_schedule. Billed per run — one credit per run. */
  ReportRun = "report-run",
  /**
   * System-initiated sessions involving reports, including:
   * - Creating a report from an agent session.
   * Generally billed per session.
   */
  ReportSystem = "report-system",
  /** Agent session initiated from a Slack @mention. */
  Slack = "slack",
  /** Hightouch's agent's session for a project or task, started by a comment or status move. */
  ProjectsSystem = "projects-system",
  /**
   * Lifecycle Studio campaign generated from an Asana task, via an Asana app
   * component (rule action or modal form) rather than by a user in-product.
   */
  Asana = "asana",
  BackgroundAgent = "background-agent",
  CopyReferencesIngestion = "copy-references-ingestion",
  /** Sessions created by Evalite eval suites in CI. */
  Eval = "eval",
  /**
   * AID (Decision Engine) sessions — variant suggestions, AID-driven
   * reports, and any other session whose context carries a
   * `decisionEngineFlowId`. Gated upstream by DE entitlement at the
   * page / scheduler layer, not by agent flags. Not billed via agent
   * credits — AID has separate pricing as part of Decision Engine.
   * createAgentSession's override stamps any session with an AID
   * context as `Aid` regardless of caller intent.
   */
  Aid = "aid",
  /**
   * Hightouch-employee-initiated insight generation for an initiative goal.
   * Gated upstream by the agenticInitiatives flag plus an HT-staff requirement
   * on the resolver, not by the workspace's agent entitlement.
   */
  InitiativeInsights = "initiative-insights",
  /** Agent session initiated from the home page. */
  Home = "home",
  /**
   * Legacy: headless context-library reviews from the retired Context Hub review tab.
   * No longer created; kept because existing sessions carry it. Not billed via agent
   * credits (agent-usage-credits.ts bills by explicit allow-list).
   */
  ContextReview = "context-review",
  /** Context Hub chat uses generic session creation, gated on anyAgentProduct and excluded from agent credits. */
  ContextHub = "context-hub",
}

/**
 * Represents the source/origin of a content email.
 * Used to track where emails came from and filter appropriately in the UI.
 */
export enum ContentEmailSource {
  /** Email created directly via standalone generation (not from a concept) */
  Standalone = "standalone",
  /** Email created from a concept but not yet saved/approved */
  ConceptDraft = "concept_draft",
  /** Email created from a concept and has been saved/approved */
  ConceptSaved = "concept_saved",
  /**
   * Email generated as a candidate for an `lcm_message_plans` row.
   * Curation state (draft/saved, see `MessageStatus`) is owned by the
   * linked `lcm_message_candidates.status`, not by this enum — so no
   * separate draft/saved variant.
   */
  LcmMessagePlan = "lcm_message_plan",
  /**
   * Template imported by a user (pasted HTML / EML / from a provider). Unlike
   * generated emails, templates are the user's starting point for new
   * generations and can be edited via the per-template agent session
   * (`generation` context flagged `isTemplate`). This is the canonical "is a
   * template" discriminator — do not rely on `llm_chat_session_id IS NULL`
   * (templates now own a session) or `is_template_enabled`.
   */
  StandaloneTemplate = "standalone_template",
  /**
   * Ephemeral reference input minted from a `cep_email_templates` (Native
   * Delivery) row when a user picks an ND template in the agent's
   * reference-email dialog. Never a library item: hidden from the template
   * library, the saved-messages list, and template-agent selection. Repeat
   * picks mint duplicate rows — accepted, like abandoned concept drafts.
   * Importing an ND template INTO the library stays a deliberate act via the
   * import flow (`standalone_template`).
   */
  NdTemplateReference = "nd_template_reference",
  /**
   * Email produced by a Figma import that has not yet been promoted to the
   * template library. It already exists as a real `content_email` (created
   * once the first authoring pass finishes) and is edited in the normal email
   * editor, but is excluded from template lists/pickers and from per-template
   * agent selection until promote flips it to `standalone_template`. Provenance
   * stays permanently in `external_provider_type = "figma_import"`.
   */
  FigmaImport = "figma_import",
}

/**
 * Whether a `content_emails.source` surfaces as a standalone row in the
 * `saved_messages` view. Mirrors the `ce.source NOT IN (...)` exclusion list in
 * the view's standalone arm (lcm_saved_messages_organization migration).
 * Exhaustive over `ContentEmailSource` so adding a new source forces an
 * explicit decision here. The view includes by allowlist, so an unknown source
 * is hidden by default — marking a new source `true` here also requires a
 * migration redefining the view. Verified against the live view definition in
 * saved-messages-view.test.ts.
 */
export const SURFACES_IN_SAVED_MESSAGES: Record<ContentEmailSource, boolean> = {
  [ContentEmailSource.Standalone]: true,
  [ContentEmailSource.ConceptSaved]: true,
  [ContentEmailSource.ConceptDraft]: false,
  [ContentEmailSource.LcmMessagePlan]: false,
  [ContentEmailSource.StandaloneTemplate]: false,
  [ContentEmailSource.NdTemplateReference]: false,
  [ContentEmailSource.FigmaImport]: false,
};

/**
 * Whether a `content_emails.source` is offered by the email pickers (the ND
 * "Choose an existing message" dialog and the agent's reference-email panel).
 * Exhaustive so adding a source forces an explicit decision — the pickers
 * previously hardcoded their own exclusion lists, which is how hidden
 * reference mints leaked into them. False = internal artifact, never
 * user-pickable.
 */
export const SELECTABLE_IN_EMAIL_PICKERS: Record<ContentEmailSource, boolean> =
  {
    [ContentEmailSource.Standalone]: true,
    [ContentEmailSource.ConceptSaved]: true,
    [ContentEmailSource.ConceptDraft]: false,
    [ContentEmailSource.LcmMessagePlan]: true,
    [ContentEmailSource.StandaloneTemplate]: true,
    [ContentEmailSource.NdTemplateReference]: false,
    [ContentEmailSource.FigmaImport]: false,
  };

/** The `_nin` form of {@link SELECTABLE_IN_EMAIL_PICKERS}, for Hasura filters
 *  and SQL. */
export const EMAIL_PICKER_EXCLUDED_SOURCES = Object.entries(
  SELECTABLE_IN_EMAIL_PICKERS,
)
  .filter(([, selectable]) => !selectable)
  .map(([source]) => source as ContentEmailSource);

/**
 * Whether a `content_emails.source` should be treated as a library template for
 * editor/agent purposes. True for promoted templates (`standalone_template`)
 * **and** pending Figma imports (`figma_import`) — a pending import behaves like
 * a template throughout its review/iterate phase (template toolbar, no
 * campaign-style artifact review) and only graduates to `standalone_template` at
 * promote. Use this instead of inline `=== "standalone_template"` checks.
 */
export function isTemplateSource(
  source: ContentEmailSource | string | null | undefined,
): boolean {
  return (
    source === ContentEmailSource.StandaloneTemplate ||
    source === ContentEmailSource.FigmaImport
  );
}

/**
 * Status of a marketing_campaign_message or lcm_message_candidate.
 * Used to track whether a message has been saved (finalized) or is still a draft.
 * This is channel-agnostic and works for email, push, SMS, etc.
 */
export enum MessageStatus {
  Draft = "draft",
  Saved = "saved",
}

// Inference backend options for the agent runtime
export type InferenceBackend =
  | "anthropic"
  | "gcp"
  | "snowflake"
  | "fireworks"
  | "databricks"
  | "gateway";

/**
 * True whatever prefix a backend adds: `anthropic/claude-opus-4-8` on the
 * gateway, `databricks-claude-sonnet-4-5` on Databricks.
 */
export function isClaudeModel(model: string): boolean {
  return model.toLowerCase().includes("claude");
}

export interface AgentContext {
  answerEngine?: { siteId: string };
  figmaConnectionId?: string;
  figmaSourceId?: string;
  workspaceId: number;
  // Optional because some sessions are system-generated (e.g. scheduled reports)
  // and have no associated user. Tools that need user scoping should fail closed
  // when this is undefined rather than falling back to workspace-wide access.
  userId?: number;
  /**
   * Owning session's agent kind (`llm_chat_sessions.type` / {@link AgentType}).
   * Authoritative value is set when context is loaded from the session — use for
   * LD `agent-session` targeting, not a stale copy from `session.context`.
   */
  agentType?: string;
  // When true, the session was created by a Hightouch employee impersonating
  // into this workspace. Impersonator sessions skip user-scoped access controls
  // (e.g. created_by filters) so employees can debug across all workspace sessions.
  isGuestAdmin?: boolean;
  parentModelId?: string | null;
  // Explicit "No parent model" choice at session creation; blocks the
  // agentic-settings backfill (`parentModelId: null` reads as merely unset).
  // Precedence: only consulted when `parentModelId` is null/absent — a
  // context carrying both silently uses the model.
  noParentModel?: boolean;
  generalModelIds?: string[];
  spaceId?: string;
  internalCustomGuidelines?: string;
  systemPromptOverrides?: Partial<Record<AgentType, string>>;
  // When true, prompts drop their "use emoji" formatting guidance and the base
  // system prompt instructs the agent to never emit emojis.
  disableEmojis?: boolean;
  subAgentClientConfig?: LLMClientConfig;
  isSkill?: boolean;

  // SDK session ID for maintaining conversation history across requests
  sdkSessionId?: string;

  // The `llm_chat_sessions` id this context belongs to. Lets brand-aware
  // readers (e.g. the ad-generation system prompt) resolve the session's
  // brand profile via `resolveBrandProfileForSession`, so `getSettings` scopes
  // asset types to that profile instead of returning every brand's groups.
  // Distinct from `sdkSessionId` (the Claude SDK conversation id).
  sessionId?: string;
  // Root interaction whose acting user determines tool availability.
  rootInteractionId?: string;

  // Input/Output directories for the agent
  inputDir?: string;
  outputDir?: string;
  // Working files the runtime never uploads with the response.
  tmpDir?: string;
  skillsDir?: string;

  // Caller-resolved cap on ads materialized on the session disk at once, also
  // the bulk-batch size injected into the ad-generation system prompt as
  // `{{maxAdsOnDisk}}`. Set on the enhanced context in claude-agent.ts so the
  // skill builder and the runtime payload use the same number. Optional on this
  // broad base context (constructed in ~150 places with no cap); the skill
  // builder defaults to DEFAULT_MAX_ADS_ON_DISK.
  maxAdsOnDisk?: number;

  // Whether this chat makes Native Delivery emails (`chatRoutesEmailToNativeDelivery`), computed once per context build so every skill reads one answer.
  ndEmailRouting?: boolean;

  // Inference backend: "anthropic" (default) or "gcp" (Vertex AI)
  inferenceBackend?: InferenceBackend;

  // ID of the interaction that created this session (for fan-out tracking).
  // This is null for user-initiated sessions and set for sessions created
  // by tool calls during a specific interaction.
  createdByInteractionId?: string | null;

  // LCM Campaign ID (if applicable). Used to load campaign artifacts
  // as reference files for any session associated with a campaign.
  marketingCampaignId?: string;

  // Scheduled report delivery options carried with the session/job context snapshot.
  reportOptions?: {
    attachPdf?: boolean;
    attachMarkdown?: boolean;
  };

  /** Controls which image editing capabilities the agent is allowed to use.
   *  Omitted keys default to enabled (true). Set to false to disable. */
  imageEditingCapabilities?: ImageEditingCapabilities;
}

/** Whether the session's context carries a usable parent model. */
export const sessionHasParentModel = (
  context: Pick<AgentContext, "parentModelId">,
): boolean => Boolean(context.parentModelId);

export type ImageEditingCapabilities = {
  text?: boolean;
  resize?: boolean;
  compositing?: boolean;
  restyle?: boolean;
  generate?: boolean;
};

// Runtime companion to the type above — `satisfies` keeps the two in lockstep.
export const IMAGE_EDITING_CAPABILITY_NAMES = [
  "text",
  "resize",
  "compositing",
  "restyle",
  "generate",
] as const satisfies readonly (keyof ImageEditingCapabilities)[];

export type ImageEditingCapabilityName =
  (typeof IMAGE_EDITING_CAPABILITY_NAMES)[number];

export interface AgentSettingsFile {
  citationScope?: CitationScope;
  filename: string;
  filetype: string;
  filesize: number;
  s3_key: string;
  description?: string | null;
  extracted_text_s3_key?: string | null;
  /**
   * Settings-entity id this file came from, so a citation (`entity:<id>`)
   * can be mapped to the staged file. Absent on files that don't originate
   * from a settings entity.
   */
  entity_id?: string;
  contextType?: ContextFileType;
}

// Fold size_channel into ContextFileType once channel guidance migrates to context entities.
export type MarkdownContextType = ContextFileType | "size_channel";

/**
 * A markdown document that provides context to the LLM agent.
 * Documents are written to the filesystem for the agent to read on demand.
 */
export type MarkdownDocument = {
  citationScope?: CitationScope;
  id: string; // UUID for stable reference
  versionId?: string; // UUID for the version of the document in the new agentic settings entitity versioning system
  name: string; // Display name (e.g., "Brand Guidelines")
  description?: string; // What this document contains - shown in system prompt for on-demand docs
  content: string; // Markdown text content
  dag?: unknown; // The steps of a DAG skill, whose markdown is generated from them
  contextType?: MarkdownContextType;
};

// TODO: Make these settings composed from individual agent settings
export interface Settings extends AgentContext {
  tableWhitelist?: string[];
  memoriesEnabled?: boolean;
  marketingLCMPlatform?: string | null;
  contentAgent?: {
    guidelines: string;
  };
  campaignAgent?: {
    databaseConfig?: NonNullable<
      CampaignsAgentContext["campaignAgent"]
    >["databaseConfig"];
    guidelines: string;
    brandGuidelines?: string;
    brandGuidelinesFile?: AgentSettingsFile;
    referenceImages?: Array<{
      fileId?: string;
      imageUrl?: string;
      /** Base64-encoded image data - if provided, skips download */
      base64?: string;
      /** Content type (e.g., 'image/jpeg') - required when using base64 */
      contentType?: string;
      description?: string;
    }>;
    /**
     * Resolved from `amp_asset_groups` by `getSettings`, not a key of the
     * stored `agentic_settings.settings` blob (#46974 removed that dual-write).
     * A blob cast straight to `Settings` never carries it.
     */
    assetTypes?: AssetTypeDefinition[];
    company?: {
      name: string;
      url: string;
    };
  };
  businessContext?: {
    name?: string;
  };
  campaignInsights?: CampaignInsightsSettings;
}

export type AgentSuggestions = {
  suggestions: SuggestionsMessage["content"];
};

export interface AgentFlags {
  fastMode?: boolean;
}

/**
 * A review agent was named on a comment, so a reply is already reserved for it
 * and its only remaining decision is what to say.
 */
export interface SummonedAdAssetReviewContext {
  mode: "summoned";
  /** The `asset_comments.id` of the pending reply this run must fill in. */
  assetCommentId: string;
  reviewAgentId: string;
}

/**
 * A review agent that already spoke in a thread, woken by a later comment that
 * does not name it. Whether this turn produces a comment at all is the agent's
 * call: `initialize_comment` says yes and `decline_to_comment` says no.
 *
 * `assetCommentId` names a row opened in `deciding` rather than a reserved
 * reply — it exists so the thread can show an agent is looking, and becomes a
 * reply only if the agent decides the comment was for it.
 */
export interface FollowUpAdAssetReviewContext {
  mode: "followUp";
  /** The `asset_comments.id` of this run's `deciding` row. */
  assetCommentId: string;
  /** The thread the agent may reply in, and the comment that woke it. */
  threadId: string;
  triggeringCommentId: string;
  reviewAgentId: string;
}

/**
 * The comment thread a review agent is answering, and the agent answering it.
 * `respond_to_comment` reads the target from here rather than from the model, so
 * it cannot answer a different comment or write as another agent.
 */
export type AdAssetReviewContext =
  | SummonedAdAssetReviewContext
  | FollowUpAdAssetReviewContext;

/**
 * Whether no Hightouch UI is driving the turn: an external MCP caller, or a
 * project or task session's sub-agent, or another autonomous run.
 * These turns cannot wait for a user to answer a quiz or approve work.
 */
export function isUnwatchedTurn(
  interactionContext:
    | Pick<InteractionContext, "externalMcpSession" | "isAutonomousRun">
    | null
    | undefined,
): boolean {
  return Boolean(
    interactionContext?.externalMcpSession ||
      interactionContext?.isAutonomousRun,
  );
}

// Null when the session has no assumptions skill, which carries the citation rules.
export type AssumptionsSkillLoad = "preloaded" | "on-demand" | null;

// Every input besides the staged files that changes what the definitions prompt section says.
export type DefinitionsSectionRules = {
  canWriteAssumptions: boolean;
  assumptionsSkillLoad: AssumptionsSkillLoad;
  // Change requests are loaded each turn, so a resumed turn is told when they change; the file is staged only when any exist.
  changeRequestsServed: boolean;
  clarificationSkillEnabled: boolean;
};

/** A project, or a task and the project it is on. */
export interface AmpProjectOrTask {
  projectId: string;
  /** Null for the project itself. */
  taskId: string | null;
}

export interface InteractionContext {
  // The switches this turn's definitions prompt section was rendered with. The system prompt
  // is not re-sent on a resumed turn, so a later turn re-sends the section when they differ.
  definitionsSectionRules?: DefinitionsSectionRules;
  // The change requests this turn was served, from servedChangeRequestsHash.
  servedChangeRequestsHash?: string;
  // SDK session ID from the last completed interaction (for resuming sessions).
  // Queried at enqueue time to ensure we only resume from completed interactions.
  sdkSessionId?: string;
  // The async MCP worker cannot recover a guest admin's request-scoped
  // impersonation mode or selected groups from session ownership alone.
  impersonationType?:
    | "admin"
    | "readonly"
    | "readsupport"
    | "user_groups"
    | "service_readonly"
    | null;
  impersonatedUserGroupIds?: string[];
  // Brand-assets state last injected into the model as of this interaction.
  // The runtime re-injects the `<brand_assets>` block on the next turn when
  // the current assets no longer match.
  brandAssetsFingerprint?: string;
  // Whether to inject workspace artifacts as input files for the agent.
  injectAdArtifacts?: boolean;
  // LD FF to automatically trigger email review after email generation completes.
  isEmailReviewEnabled?: boolean;
  // Review-specific fields
  isReviewInteraction?: boolean;
  artifactId?: string;
  artifactVersionId?: string;
  // Email ID for feedback flow - allows direct lookup without artifact lookup
  emailId?: string;
  // Artifact type (e.g., "content_copy", "content_email") for selecting appropriate reviewers
  artifactType?: string;
  // Structured feedback items from artifact review (used instead of embedding in prompt)
  feedbackItems?: FeedbackItem[];
  // A review agent answering on an ad's comment thread, either summoned by an
  // @mention or woken by a follow-up in a thread it already spoke in. Its
  // presence selects the ad-asset-review skill, suppresses the ad-generation
  // skills, and narrows the agent to read-only tools.
  adAssetReview?: AdAssetReviewContext;
  // Ad Studio project ID for grouping pipeline runs
  adStudioProjectId?: string;
  // Ad Studio style ID: the style bound to an ad-generation session. A bound
  // style id (a real UUID) takes precedence over adStudioStyleMode.
  adStudioStyleId?: string;
  // Ad Studio style selection mode when no specific style is bound:
  // "auto" = the agent must pick the most relevant style for the brief;
  // "none" = the user explicitly chose to generate without a style.
  adStudioStyleMode?: AdStudioStyleMode;
  // The style chosen for the emails a canvas turn starts, carried to each
  // email's first turn, where image generation is pointed at it.
  emailStyleId?: string;
  // Where the ads this turn starts go on their canvas: the first here, the rest beside it in a row.
  canvasPosition?: { x: number; y: number };
  // Session toggle; when on, the agent must search Savee for reference imagery
  // when the user's creative request has no clear visual direction. Default off.
  creativeBoost?: boolean;
  // A canvas turn's choice of the beta video engine for the ads it starts. Each
  // ad's session takes it as its session-scoped `useVideoEngine` at creation.
  useVideoEngine?: boolean;
  // The `ad_brief_asset_versions.id` that is being decomposed via "async decomposition" in this interaction.
  decompositionAssetVersionId?: string;
  // Hidden mockup build for an automated Ideas feed idea (`emit_idea` in
  // `lib/llm/skills/ideas-emit/emit-idea-tool.ts`). Tagged so telemetry can
  // separate these from user-driven project sessions.
  isIdeasFeedBuild?: boolean;
  // When true, sets ad_studio_projects.llm_chat_session_id to this session.
  // Only the project-level chat should set this; per-asset and size-variant
  // sessions pass adStudioProjectId for context but must not clobber the link.
  linkToAdStudioProject?: boolean;
  // Links the new session to an ad_brief_asset — the per-asset analogue of
  // linkToAdStudioProject, so edit sessions can be created through the generic
  // createAgentSession mutation instead of a bespoke resolver. Without
  // channelSizeId the session id is written to llm_chat_session_id; with it,
  // into the size_variant_sessions entry for that size.
  linkToAdBriefAsset?: { assetId: string; channelSizeId?: string };
  // Makes the new session this ND email message's one chat, stored on messages.llm_chat_session_id.
  // Only the createAgentSession resolver sets it, after the ND refusals; other mutations ignore it.
  linkToNdMessage?: { messageId: string };
  // ID of a follow-up interaction that was automatically triggered (e.g., artifact review)
  // Used by the frontend to discover and poll the new interaction
  nextInteractionId?: string;
  // Agent execution mode selected by the user in the UI
  agentMode?: AgentMode;
  // The session was created through the external MCP server, so no Hightouch
  // UI is watching it. Two consequences follow from that single fact:
  //   1. Static builds decompose synchronously (`forceSyncDecomposition` on the
  //      runtime request) — an MCP caller's only completion signal is the
  //      interaction, so a completed interaction has to mean finished, layered,
  //      editable ads rather than flat mockups awaiting a background session.
  //   2. Plan mode writes numbered treatments in prose instead of leaning on
  //      the app's quiz and brief-editor surfaces, which don't exist here.
  externalMcpSession?: boolean;
  // Number of ad concepts the user wants generated. Each entry surface
  // validates its own range: the app picker allows 1-10, external MCP 1-200.
  conceptCount?: number;
  // Content node ID selected in the SVG editor for editing
  selectedContentNodeId?: string;
  // Ad brief IDs attached as references when sending a message
  referenceAssetIds?: string[];
  // Catalog asset group whose tag schema seeds the Enriched Catalog
  // template-design context (used by the AdStudioTemplateGenerator agent).
  adStudioAssetGroupId?: string;
  // Explicitly selects the first-class Ad Studio or Enriched Catalog template workflow.
  // Entity existence does not distinguish these workflows: Ad Studio imports create draft entities.
  adStudioTemplateMode?: AdStudioTemplateMode;
  // Distinguishes first-time creation from versioning an existing template.
  // Both operations carry an entity id, so id presence is not a discriminator.
  adStudioTemplateOperation?: AdStudioTemplateOperation;
  // Import source retained as trusted interaction metadata throughout template creation.
  adStudioTemplateImportSource?: AdStudioTemplateImportSource;
  // Exact saved ad version whose HTML and component assets seed an ad import.
  adStudioTemplateSourceAd?: AdStudioTemplateSourceAd;
  // Initial display name retained outside the agent unless name inference is requested.
  adStudioTemplateName?: string;
  adStudioTemplateInferName?: boolean;
  adStudioTemplateTrackImport?: boolean;
  // Server-generated target ID shared by the draft, retries, and ready template.
  adStudioTemplateEntityId?: string;
  // First-class templates explicitly selected for this Ad Studio interaction.
  adStudioTemplateIds?: string[];
  adStudioTemplateFolderIds?: string[];
  preloadSkills?: SkillName[];
  // Run executes with no user present (e.g. scheduled reports). The system prompt
  // adds autonomous-run guidance: don't ask clarifying questions, the final
  // response is the complete deliverable.
  isAutonomousRun?: boolean;
  // Marks a figma-import follow-up interaction whose sole job is to decompose the
  // imported images into editable SVG layers and repoint the email. Set by the
  // post-interaction processor that enqueues it; used to avoid re-enqueuing the
  // decompose from the decompose run's own completion.
  isFigmaImageDecompose?: boolean;
  // Per-run LCM generation-config override for this interaction only (e.g. the
  // "Add messages" modal asking for a different candidate count / templates for
  // one batch). The message-plan skill resolves each knob as
  // `lcmConfigOverride?.X ?? context.X ?? default` (context = the campaign's
  // session context), so this never persists to the campaign's session context.
  lcmConfigOverride?: LcmCampaignConfig;
  // Tags an enqueue-only LCM agent run so the app can derive a loading state
  // while the agent works and no DB rows exist yet. The frontend only ever reads
  // this off a *pending* interaction, so it self-clears when the run finishes —
  // no separate status to keep in sync. Discriminated on `type` so future
  // single-intent runs slot in without touching existing readers (e.g. a
  // `{ type: "editing_plan"; planId }` if we ever surface a per-plan edit state).
  lcmActivity?: LcmActivity;
  // The derived ad a hidden resize session was enqueued to make. The app reads
  // it off a pending interaction to show the size before the ad exists.
  adResize?: AdResizeRequest;
}

/** One requested derived ad: the size it is cut at and the ad it is cut from. */
export type AdResizeRequest = {
  sourceAssetId: string;
  /** The source's head at request time. */
  sourceVersionId: string | null;
  /** Null when the derived ad is made untagged. */
  channelSizeId: string | null;
  width: number;
  height: number;
  /** The derived ad this request retired and replaces. */
  replacesAssetId?: string;
  /** The empty size asset holding this resize's place on a canvas, which its save fills. */
  pendingAssetId?: string;
};

/**
 * Enqueue-only LCM agent run tagged on an interaction's context so the app can
 * show a loading placeholder before any DB rows exist. Discriminated on `type`.
 */
export type LcmActivity =
  | { type: "generating_plans" }
  | {
      type: "generating_candidates";
      planId: string;
      expectedCount: number;
      initialCandidateCount?: number;
    }
  | {
      type: "generating_dynamic_content";
      /** Document-order index of the target element in the displayed email
       * version's body (`querySelectorAll("*")`) — no slot id exists yet (the
       * agent mints it mid-run), so the element is the anchor the canvas
       * hangs the loading lane on. Only valid against the version html the
       * user was viewing at enqueue, which the canvas keeps displaying for
       * the run's lifetime. */
      elementIndex: number;
      /** Display label for the element ("Image", "Text", …) — skeleton pill
       * copy, never used for lookup. */
      elementType: string;
      expectedCount: number;
    }
  | {
      type: "generating_message_dynamic_content";
      /** Skeleton block id (artifact content.blocks) of the text block being
       * made dynamic — no slot id exists yet (the agent mints it mid-run), so
       * the block is the anchor the SMS/push canvas hangs its pending lane
       * on. Only valid against the version the user was viewing at enqueue. */
      blockId: string;
      expectedCount: number;
    }
  | {
      type: "migrating_compile_target";
      targetEspName: string;
      assignmentSource: "audience_schema" | "esp_profile";
      conditionCount: number;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Narrow jsonb `lcmActivity` from an interaction's context. Invalid or
 * unknown shapes are dropped rather than trusted — the tag is UI-only.
 */
export function parseLcmActivity(value: unknown): LcmActivity | undefined {
  if (!isRecord(value) || typeof value.type !== "string") return undefined;
  switch (value.type) {
    case "generating_plans":
      return { type: "generating_plans" };
    case "generating_candidates": {
      if (
        typeof value.planId !== "string" ||
        typeof value.expectedCount !== "number"
      ) {
        return undefined;
      }
      return {
        type: "generating_candidates",
        planId: value.planId,
        expectedCount: value.expectedCount,
        ...(typeof value.initialCandidateCount === "number"
          ? { initialCandidateCount: value.initialCandidateCount }
          : {}),
      };
    }
    case "generating_dynamic_content": {
      if (
        typeof value.elementIndex !== "number" ||
        typeof value.elementType !== "string" ||
        typeof value.expectedCount !== "number"
      ) {
        return undefined;
      }
      return {
        type: "generating_dynamic_content",
        elementIndex: value.elementIndex,
        elementType: value.elementType,
        expectedCount: value.expectedCount,
      };
    }
    case "generating_message_dynamic_content": {
      if (
        typeof value.blockId !== "string" ||
        typeof value.expectedCount !== "number"
      ) {
        return undefined;
      }
      return {
        type: "generating_message_dynamic_content",
        blockId: value.blockId,
        expectedCount: value.expectedCount,
      };
    }
    case "migrating_compile_target": {
      if (
        typeof value.targetEspName !== "string" ||
        (value.assignmentSource !== "audience_schema" &&
          value.assignmentSource !== "esp_profile") ||
        typeof value.conditionCount !== "number"
      ) {
        return undefined;
      }
      return {
        type: "migrating_compile_target",
        targetEspName: value.targetEspName,
        assignmentSource: value.assignmentSource,
        conditionCount: value.conditionCount,
      };
    }
    default:
      return undefined;
  }
}
